import { useCallback, useEffect, useRef, useState } from 'react';
import { WAKE_ACK_TEXT, WAKE_PHRASE } from '../config/cocoVoice';
import {
  isMicrophoneSupported,
  playAudioBase64,
  preloadWakeAckAudio,
  playWakeAckUrl,
} from '../services/cocoVoiceApi';
import { createCocoVoiceWsListener } from '../utils/cocoVoiceWsListener';
import { speakLocalWakeAck } from '../utils/cocoWakeLocal';

function MicIcon({ active, paused }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 15a3 3 0 0 0 3-3V6a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z"
        fill={active && !paused ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M19 11a7 7 0 0 1-14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {paused ? (
        <path d="M5 5l14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      ) : null}
    </svg>
  );
}

/** 默认使用麦克风推流 + 服务端识别（不依赖 Google 语音服务） */
const ENGINE_PCM = 'pcm';

export default function LocalVoiceWakeAssistant({ compact = false }) {
  const engineRef = useRef(ENGINE_PCM);
  const wsListenerRef = useRef(null);
  const runningRef = useRef(false);
  const awakeRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [engineLabel, setEngineLabel] = useState('服务端识别');
  const [running, setRunning] = useState(false);
  const [awake, setAwake] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [responseText, setResponseText] = useState('');
  const [hintText, setHintText] = useState('');
  const [errorText, setErrorText] = useState('');

  const triggerWake = useCallback(async (payload) => {
    awakeRef.current = true;
    setAwake(true);
    setResponseText(WAKE_ACK_TEXT);
    setPhase('awake');
    const heard = payload?.text ? `（${payload.text}）` : '';
    setHintText(`已唤醒${heard}，请说指令`);
    setErrorText('');

    try {
      const url = await preloadWakeAckAudio();
      await playWakeAckUrl(url);
    } catch {
      await speakLocalWakeAck(WAKE_ACK_TEXT);
    }

    if (runningRef.current) {
      setPhase('listening');
      setHintText('聆听中 · 已唤醒，请说指令');
    }
  }, []);

  useEffect(() => {
    setReady(isMicrophoneSupported());
    setHintText('点击麦克风开始聆听，说「小可同学」唤醒');
  }, []);

  useEffect(() => () => {
    runningRef.current = false;
    wsListenerRef.current?.stop?.();
    wsListenerRef.current = null;
    if (typeof window !== 'undefined') window.speechSynthesis?.cancel?.();
  }, []);

  const stopAll = async () => {
    await wsListenerRef.current?.stop?.();
    wsListenerRef.current = null;
    runningRef.current = false;
    awakeRef.current = false;
    setRunning(false);
    setAwake(false);
    setPhase('stopped');
    setHintText('已停止聆听，点击麦克风重新开始');
  };

  const startPcmEngine = async () => {
    engineRef.current = ENGINE_PCM;
    setEngineLabel('服务端识别');
    setErrorText('');
    setHintText('正在连接语音服务…');
    setPhase('starting');

    const listener = createCocoVoiceWsListener({
      onState: (state) => {
        if (state === 'connecting' || state === 'reconnecting') {
          setPhase('starting');
          setHintText('正在连接语音服务…');
        } else if (state === 'listening' || state === 'ready') {
          if (!awakeRef.current) {
            setPhase('listening');
            setHintText(`聆听中 · 说「${WAKE_PHRASE}」唤醒`);
          }
        } else if (state === 'speech' || state === 'transcript') {
          setPhase('speech');
        } else if (state === 'processing') {
          setPhase('processing');
          setHintText('识别中…');
        }
      },
      onWake: () => triggerWake({ source: 'server' }),
      onResponse: async (data) => {
        if (data?.response_text) setResponseText(data.response_text);
        if (data?.audio_base64) {
          try {
            listener.setMutedForTts(true);
            await playAudioBase64(data.audio_base64, data.mime_type);
          } catch {
            /* ignore */
          } finally {
            listener.setMutedForTts(false);
          }
        }
      },
      onHint: (hint) => {
        if (hint) {
          setHintText(hint);
          if (/听到/.test(hint)) setPhase('speech');
        }
      },
      onError: (err) => {
        setErrorText(err?.message || '语音服务异常');
        setPhase('error');
        setListeningFalse();
      },
    });

    wsListenerRef.current = listener;
    await listener.start();
    runningRef.current = true;
    setRunning(true);
    setPhase('listening');
    setHintText(`聆听中 · 说「${WAKE_PHRASE}」唤醒`);
  };

  const setListeningFalse = () => {
    setRunning(false);
    runningRef.current = false;
  };

  const toggleListening = async () => {
    if (!ready) return;

    if (running) {
      await stopAll();
      return;
    }

    setResponseText('');
    awakeRef.current = false;
    setAwake(false);

    try {
      await startPcmEngine();
    } catch (e) {
      setErrorText(e?.message || '无法启动语音识别');
      setPhase('error');
    }
  };

  const handleManualWake = async () => {
    if (!runningRef.current) {
      setErrorText('');
      try {
        await startPcmEngine();
      } catch (e) {
        setErrorText(e?.message || '无法启动语音服务');
        return;
      }
    }
    await triggerWake({ text: WAKE_PHRASE, source: 'manual' });
  };

  if (!ready) {
    return (
      <div className={`coco-voice${compact ? ' coco-voice--compact' : ''}`}>
        <div className="coco-voice-unsupported">
          请使用 Chrome / Edge 浏览器并允许麦克风权限
        </div>
      </div>
    );
  }

  const statusLine = (() => {
    if (errorText) return errorText;
    if (hintText && phase !== 'speech') return hintText;
    if (phase === 'starting') return hintText || '正在连接语音服务…';
    if (phase === 'stopped' || phase === 'idle') {
      return hintText || `点击麦克风开始聆听，说「${WAKE_PHRASE}」唤醒`;
    }
    if (phase === 'speech') return hintText || '正在识别…';
    if (phase === 'processing') return '识别中…';
    if (phase === 'awake') return hintText || WAKE_ACK_TEXT;
    if (running) return hintText || `聆听中 · 说「${WAKE_PHRASE}」`;
    return '语音助手未就绪';
  })();

  const micActive = running && (phase === 'listening' || phase === 'speech' || phase === 'awake');

  return (
    <div className={`coco-voice${compact ? ' coco-voice--compact' : ''}`}>
      <div className="coco-voice-head">
        <span className="service-block-label">语音助手</span>
        <span className="coco-voice-wake-tip">
          唤醒词：{WAKE_PHRASE} · {engineLabel}
        </span>
      </div>

      <div className="coco-voice-main">
        <button
          type="button"
          className={`coco-voice-mic-btn${micActive ? ' is-recording' : ''}${running ? ' active' : ''}${awake ? ' is-busy' : ''}`}
          onClick={toggleListening}
          aria-label={running ? '停止聆听' : '开始聆听'}
        >
          <MicIcon active={micActive} paused={!running} />
          <span className="coco-voice-mic-label">
            {running ? (awake ? '已唤醒' : (phase === 'starting' ? '连接中' : '聆听中')) : '点击开始'}
          </span>
          {phase === 'speech' ? <span className="coco-voice-pulse" aria-hidden="true" /> : null}
        </button>

        <div className="coco-voice-body">
          <p className={`coco-voice-status${errorText ? ' is-error' : ''}`}>{statusLine}</p>
          {responseText ? (
            <div className="coco-voice-response" aria-live="polite">
              {responseText}
            </div>
          ) : null}
          <button type="button" className="coco-voice-manual-wake" onClick={handleManualWake}>
            识别不到？点这里手动唤醒
          </button>
        </div>
      </div>
    </div>
  );
}
